'use client'

import { useCallback } from 'react'

import type { BackendHttpClientSideOptions, FetchType } from '@types'

import { basePath } from '@lib/constants'
import { baseFetch } from '@lib/http'

import useLocale from './client/useLocale'

const defaultHeaders: HeadersInit = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
}

export type HttpMethods = {
  get: <JSON>(path: string, options?: BackendHttpClientSideOptions) => Promise<JSON | undefined>
  clientFetch: <JSON>(input: RequestInfo, init?: RequestInit, type?: FetchType) => Promise<JSON | undefined>
}

export default function useHttpRequest(): HttpMethods {
  const locale = useLocale()

  const getBaseUrl = useCallback((): string => `${basePath || '/'}/api/${locale}/`.replace(/\/\//g, '/'), [locale])

  const generateHeaders = useCallback((options?: BackendHttpClientSideOptions): HeadersInit => {
    const headers: HeadersInit = { ...defaultHeaders, ...options?.headers }

    if (options?.type === 'blob') delete headers['Accept' as keyof HeadersInit]
    else if (options?.type === 'text') headers['Accept' as keyof HeadersInit] = 'text/plain'

    return headers
  }, [])

  const clientFetch = useCallback(async function clientFetchImplementation<JSON>(
    input: RequestInfo,
    init?: RequestInit,
    type?: FetchType,
  ): Promise<JSON | undefined> {
    const res = await baseFetch(input, init)

    switch (type) {
      case 'text':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return res.text() as any
      case 'blob':
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return res.blob() as any
      default:
        return res.json()
    }
  }, [])

  const get = useCallback(
    function getImplementation<JSON>(path: string, options?: BackendHttpClientSideOptions): Promise<JSON | undefined> {
      return clientFetch<JSON>(
        getBaseUrl() + path,
        { method: 'GET', headers: generateHeaders(options) },
        options?.type || 'json',
      )
    },
    [clientFetch, getBaseUrl, generateHeaders],
  )

  return { get, clientFetch }
}
